import { forwardRef } from 'react';

import { useRender } from '@base-ui/react/use-render';

import { cn } from '../utils';
import { textLinkIconRecipe, textLinkRecipe } from './TextLink.recipe';

import type { TextLinkProps } from './types';

/**
 * Design System 2.0 link for use INSIDE a sentence.
 *
 * `ButtonLink` cannot do this job: it carries the button's height, padding and a flex content wrapper
 * (so it cannot wrap across lines) and it replaces `children` (so it cannot be a `Trans` slot, where
 * `Trans` injects the translated words as children). This is a plain anchor that inherits the
 * sentence's font and colour, so it works in both:
 *
 *   <Trans i18nKey="agreement" components={{ eula: <TextLink href={eulaUrl} external externalLabel={t('newTab')} /> }} />
 *
 * `render` composes a router link without this layer depending on one: `render={<Link to="/x" />}`.
 *
 * Trailing punctuation belongs OUTSIDE the link text. A link is not a button: no padding, no height,
 * no hit area beyond the line box, which is the standing exception WCAG 2.5.8 makes for inline links.
 *
 * `standalone` is the other job: an action on a line of its own (Show more, Back, Skip), flush with the
 * text around it. It adds `startIcon` / `endIcon` (16px), `appearance="accent"` for wayfinding, and an
 * invisible tap area, and it renders a `<button>` through `render={<button type="button" />}` when it
 * acts rather than navigates. Neither belongs on a link inside a sentence, so the types refuse them
 * there. Inside a row of controls (toolbar, form footer, card actions) use `Button` instead.
 *
 * `external` adds `target="_blank"`, `rel="noopener noreferrer"` and a visually hidden announcement.
 * It draws no glyph yet: the DS 2.0 icon set has no external-link icon (logged in `natter-ds-gaps`).
 */
export const TextLink = forwardRef<HTMLElement, TextLinkProps>(
  (
    { external, externalLabel, standalone, appearance, startIcon, endIcon, className, render, children, ...rest },
    ref
  ) =>
    useRender({
      ref,
      render: render ?? <a />,
      props: {
        className: cn(
          textLinkRecipe({ standalone: !!standalone, appearance: standalone ? appearance : undefined }),
          className
        ),
        ...(external && { target: '_blank', rel: 'noopener noreferrer' }),
        children: (
          <>
            {startIcon ? <span className={textLinkIconRecipe()}>{startIcon}</span> : null}
            {children}
            {endIcon ? <span className={textLinkIconRecipe()}>{endIcon}</span> : null}
            {external && <span className="sr-only"> ({externalLabel})</span>}
          </>
        ),
        ...rest,
      },
    })
);
